/*
 * Google Analytics (gtag.js) for the public SPA - the port of the legacy
 * layouts' header snippet (`ViewHelper::getHeaderScript`), driven by the same
 * admin-managed `general.analytics_id` setting (read via `GET /api/site/settings`).
 *
 * The CSP keeps `script-src 'self'` + Google's tag host, so there is no inline
 * bootstrap snippet: `dataLayer`/`gtag` are defined here, in the bundle, and only
 * the gtag.js loader is injected. The automatic page_view is switched off - an
 * SPA changes routes without a page load, so the layout reports every
 * navigation itself (`trackPageView`).
 */

const GTAG_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js';

/** Marks the injected loader so a second call (StrictMode, re-render) is a no-op. */
const LOADER_ATTRIBUTE = 'data-analytics-id';

/**
 * GA4 measurement id (`G-XXXXXXX`) or a legacy Universal Analytics property (`UA-1234567-1`).
 * The admin save validates the same shape (`UpdateGeneralSettingsRequest`) - keep both in step.
 */
const ANALYTICS_ID_PATTERNS: readonly RegExp[] = [/^G-[A-Z0-9]{4,20}$/, /^UA-\d{4,10}-\d{1,4}$/];

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

export interface PageView {
  /** Router path + query string, e.g. `/game?page=2`. */
  path: string;
  title: string;
  /** Absolute URL of the page. */
  location: string;
}

/**
 * Analytics runs in production builds only - never in the Vite dev server (a
 * local stack may carry a copied analytics id) or in tests.
 */
export function isAnalyticsEnabled(): boolean {
  return import.meta.env.PROD;
}

/**
 * Only well-formed ids reach the loader URL: the value is admin-entered free
 * text, so anything else is refused rather than interpolated into a script src.
 */
export function isValidAnalyticsId(id: string): boolean {
  return ANALYTICS_ID_PATTERNS.some((pattern) => pattern.test(id));
}

/**
 * Defines `gtag` and injects gtag.js for `id`. Idempotent - once a loader is on
 * the page, later calls do nothing (an id changed mid-visit applies on the next
 * page load). Returns whether analytics is active after the call.
 */
export function loadAnalytics(id: string): boolean {
  if (document.querySelector(`script[${LOADER_ATTRIBUTE}]`) !== null) return true;
  if (!isValidAnalyticsId(id)) return false;

  const dataLayer = window.dataLayer ?? [];
  window.dataLayer = dataLayer;
  // gtag.js reads `arguments` objects off the dataLayer, not arrays - this is
  // Google's documented bootstrap, just moved out of an inline <script>.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `${GTAG_SCRIPT_URL}?id=${encodeURIComponent(id)}`;
  script.setAttribute(LOADER_ATTRIBUTE, id);
  document.head.appendChild(script);

  return true;
}

/** Reports one SPA navigation; a no-op until `loadAnalytics` has run. */
export function trackPageView({ path, title, location }: PageView): void {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: location,
  });
}
